import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { authClient } from "@/lib/auth/auth-client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, CheckCircle2, AlertCircle, MinusCircle, Send, Upload, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import {
 loadBatchesFn,
 loadBulkProvidersFn,
 loadNotificationsFn,
} from "./-dashboard.admin.bulk-imports.functions";

export const Route = createFileRoute("/dashboard/admin/bulk-imports")({
 component: BulkImportsAuditPage,
});

type Batch = {
 id: string;
 created_at: string;
 source: string;
 total_rows: number;
 succeeded: number;
 failed: number;
 notes: string | null;
};

type ProviderRow = {
 id: string;
 name: string;
 practice: string;
 email: string | null;
 city: string | null;
 state: string | null;
 claimed_by: string | null;
 created_at: string;
 bulk_import_batch_id: string | null;
};

type NotifRow = {
 id: string;
 listing_id: string | null;
 notification_type: string;
 send_status: string;
 error_message: string | null;
 sent_at: string | null;
 created_at: string;
 recipient_email: string;
};

function BulkImportsAuditPage() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const [busy, setBusy] = useState(true);
 const [batches, setBatches] = useState<Batch[]>([]);
 const [providers, setProviders] = useState<ProviderRow[]>([]);
 const [notifByListing, setNotifByListing] = useState<Record<string, NotifRow[]>>({});
 const [resendingId, setResendingId] = useState<string | null>(null);
 const [selectedBatch, setSelectedBatch] = useState<string | "all">("all");

 // ---- Drive importer state ----
 const [driveFileId, setDriveFileId] = useState("1b08TvT1u4di4KhwWQrkglkqlZNY13em7");
 const [importing, setImporting] = useState(false);
 const [paused, setPaused] = useState(false);
 const pausedRef = useRef(false);
 const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
 const [progress, setProgress] = useState<{
 byte_offset: number; total_bytes: number | null;
 processed_rows: number; succeeded: number; skipped_dupes: number; failed: number;
 } | null>(null);

 const reload = async () => {
 setBusy(true);
 const [b, p] = await Promise.all([
 loadBatchesFn(),
 loadBulkProvidersFn(),
 ]);
 setBatches((b ?? []) as any);
 const provs = (p ?? []) as ProviderRow[];
 setProviders(provs);
 if (provs.length) {
 const ids = provs.map((x) => x.id);
 const n = await loadNotificationsFn({ data: { ids } });
 const grouped: Record<string, NotifRow[]> = {};
 (n ?? []).forEach((row: any) => {
 if (!row.listing_id) return;
 (grouped[row.listing_id] ??= []).push(row);
 });
 setNotifByListing(grouped);
 } else {
 setNotifByListing({});
 }
 setBusy(false);
 };

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 if (primaryRole === "admin") reload();
 }, [user, loading, primaryRole]);

 const resend = async (listingId: string) => {
 setResendingId(listingId);
 try {
 const { resendClaimEmail } = await import("@/lib/listing-email-sequence");
 const result = await resendClaimEmail(listingId);
 const r = result as any;
 if (r?.skipped) toast.warning(`Skipped: ${r.skipped}`);
 else if (r?.blocked) toast.error(`Blocked: ${(r.blocked as string[]).join(", ")}`);
 else if (r?.sent === false) toast.error(`Send failed: ${r.error ?? "unknown"}`);
 else toast.success("Claim email re-sent");
 // small delay before refetching logs
 setTimeout(() => reload(), 1200);
 } catch (e: any) {
 toast.error(e?.message ?? "Resend failed");
 } finally {
 setResendingId(null);
 }
 };

 // ---- Drive importer loop ----
 const runOneChunk = async (batchId: string | null) => {
 const { data: sess } = await authClient.getSession();
 if (!sess?.user) throw new Error("Not signed in");
  const res = await fetch("/api/public/admin/bulk-ingest", {
 method: "POST",
 credentials: "same-origin",
				headers: { "Content-Type": "application/json" },
 body: JSON.stringify({ drive_file_id: driveFileId.trim(), batch_id: batchId }),
 });
 const json = await res.json();
 if (!res.ok) throw new Error(json?.error ?? `HTTP ${res.status}`);
 return json as {
 batch_id: string; done: boolean;
 byte_offset: number; total_bytes: number | null;
 cumulative: { processed_rows: number; succeeded: number; skipped_dupes: number; failed: number };
 };
 };

 const startImport = async () => {
 if (!driveFileId.trim()) { toast.error("Enter a Drive file ID"); return; }
 setImporting(true);
 setPaused(false);
 pausedRef.current = false;
 let currentBatch: string | null = activeBatchId;
 try {
 while (true) {
 if (pausedRef.current) { toast.info("Import paused"); break; }
 const r = await runOneChunk(currentBatch);
 currentBatch = r.batch_id;
 setActiveBatchId(r.batch_id);
 setProgress({
 byte_offset: r.byte_offset,
 total_bytes: r.total_bytes,
 ...r.cumulative,
 });
 if (r.done) {
 toast.success(`Import complete: ${r.cumulative.succeeded.toLocaleString()} inserted, ${r.cumulative.skipped_dupes.toLocaleString()} skipped`);
 setActiveBatchId(null);
 await reload();
 break;
 }
 }
 } catch (e: any) {
 toast.error(`Import error: ${e?.message ?? "unknown"}`);
 } finally {
 setImporting(false);
 }
 };

 const pauseImport = () => { pausedRef.current = true; setPaused(true); };
 const resumeImport = () => { setPaused(false); startImport(); };

 if (loading || !user || primaryRole !== "admin") {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
 }

 const visibleProviders = selectedBatch === "all"
 ? providers
 : providers.filter((p) => p.bulk_import_batch_id === selectedBatch);

  return (
 <AdminPageShell
   title="Bulk Imports"
   description="Listings created via CSV import and claim-email status for each one."
   primaryAction={{ label: busy ? "Refreshing…" : "Refresh", icon: RefreshCw, onClick: reload }}
 >
 <div className="space-y-8">
 {/* ---- Google Drive importer ---- */}
 <section className="card-surface p-5 space-y-4">
 <div className="flex items-center gap-2">
 <Upload className="h-4 w-4 text-primary" />
 <h2 className="text-sm font-semibold uppercase tracking-wider font-display">Import providers from Google Drive</h2>
 </div>
 <p className="text-xs text-muted-foreground">
 Streams the CSV directly from Drive in 6&nbsp;MB chunks, dedups on NPI, and inserts in batches of 1,000.
 Notifications are paused for the whole import. Safe to pause and resume, progress is stored on the batch.
 </p>
 <div className="flex flex-col sm:flex-row gap-2">
 <Input
 value={driveFileId}
 onChange={(e) => setDriveFileId(e.target.value)}
 placeholder="Google Drive file ID"
 disabled={importing}
 className="font-mono text-xs"
 />
 {!importing ? (
 <Button onClick={startImport} disabled={!driveFileId.trim()}>
 <Play className="h-4 w-4" /> {activeBatchId ? "Resume" : "Start import"}
 </Button>
 ) : (
 <Button onClick={pauseImport} variant="outline" disabled={paused}>
 <Pause className="h-4 w-4" /> Pause
 </Button>
 )}
 </div>
 {progress && (
 <div className="space-y-2">
 <div className="h-2 w-full bg-muted rounded overflow-hidden">
 <div
 className="h-full bg-primary transition-all"
 style={{
 width: progress.total_bytes
 ? `${Math.min(100, (progress.byte_offset / progress.total_bytes) * 100)}%`
 : "0%",
 }}
 />
 </div>
 <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
 <div><span className="text-muted-foreground">Bytes:</span> {(progress.byte_offset / 1024 / 1024).toFixed(1)} MB
 {progress.total_bytes ? ` / ${(progress.total_bytes / 1024 / 1024).toFixed(0)} MB` : ""}</div>
 <div><span className="text-muted-foreground">Rows:</span> {progress.processed_rows.toLocaleString()}</div>
 <div className="text-emerald-700"><span className="text-muted-foreground">Inserted:</span> {progress.succeeded.toLocaleString()}</div>
 <div className="text-amber-700"><span className="text-muted-foreground">Dupes:</span> {progress.skipped_dupes.toLocaleString()}</div>
 <div className="text-destructive"><span className="text-muted-foreground">Failed:</span> {progress.failed.toLocaleString()}</div>
 </div>
 {importing && (
 <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
 <Loader2 className="h-3 w-3 animate-spin" /> Processing chunk…
 </div>
 )}
 </div>
 )}
 </section>

 <section>
 <div className="flex items-center justify-between mb-3">
 <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider font-display">Recent imports</h2>
 <Button size="sm" variant="outline" onClick={reload} disabled={busy}>
 <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
 </Button>
 </div>
 <div className="card-surface-flat divide-y divide-border overflow-hidden">
 <button
 onClick={() => setSelectedBatch("all")}
 className={`w-full text-left p-3 text-sm flex items-center justify-between hover:bg-accent ${selectedBatch === "all" ? "bg-accent" : ""}`}
 >
 <span className="font-medium">All bulk-imported listings</span>
 <span className="text-xs text-muted-foreground">{providers.length} rows</span>
 </button>
 {batches.length === 0 && !busy && (
 <div className="p-4 text-sm text-muted-foreground">No bulk imports yet.</div>
 )}
 {batches.map((b) => (
 <button
 key={b.id}
 onClick={() => setSelectedBatch(b.id)}
 className={`w-full text-left p-3 text-sm grid grid-cols-2 md:grid-cols-5 gap-2 hover:bg-accent ${selectedBatch === b.id ? "bg-accent" : ""}`}
 >
 <span className="font-mono text-xs text-muted-foreground truncate">{b.id.slice(0, 8)}</span>
 <span>{new Date(b.created_at).toLocaleString()}</span>
 <span className="text-muted-foreground">{b.source}</span>
 <span><CheckCircle2 className="h-3.5 w-3.5 inline text-emerald-600" /> {b.succeeded}/{b.total_rows}</span>
 <span>{b.failed > 0 ? <span className="text-destructive"><AlertCircle className="h-3.5 w-3.5 inline" /> {b.failed} failed</span> : <span className="text-muted-foreground"> </span>}</span>
 </button>
 ))}
 </div>
 </section>

 <section>
 <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 font-display">
 Listings ({visibleProviders.length})
 </h2>
 <div className="card-surface-flat overflow-x-auto">
 <table className="w-full text-sm">
 <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
 <tr>
 <th className="text-left p-3">Practice</th>
 <th className="text-left p-3">Email</th>
 <th className="text-left p-3">Location</th>
 <th className="text-left p-3">Claim email</th>
 <th className="text-left p-3">Claimed</th>
 <th className="text-right p-3">Action</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {visibleProviders.length === 0 && !busy && (
 <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No listings.</td></tr>
 )}
 {visibleProviders.map((p) => {
 const notifs = notifByListing[p.id] ?? [];
 const claim = notifs.find((n) => n.notification_type === "listing_added") ?? notifs[0];
 return (
 <tr key={p.id}>
 <td className="p-3">
 <div className="font-medium">{p.practice}</div>
 <div className="text-xs text-muted-foreground">{p.name}</div>
 </td>
 <td className="p-3 text-xs">{p.email ?? <span className="text-muted-foreground italic">no email</span>}</td>
 <td className="p-3 text-xs">{[p.city, p.state].filter(Boolean).join(", ")}</td>
 <td className="p-3 text-xs">
 <ClaimStatus row={claim} />
 </td>
 <td className="p-3 text-xs">
 {p.claimed_by ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <MinusCircle className="h-4 w-4 text-muted-foreground" />}
 </td>
 <td className="p-3 text-right">
 <Button
 size="sm"
 variant="outline"
 disabled={!p.email || resendingId === p.id || !!p.claimed_by}
 onClick={() => resend(p.id)}
 title={p.claimed_by ? "Already claimed" : !p.email ? "No email on file" : "Force-send claim email"}
 >
 {resendingId === p.id
 ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
 : <Send className="h-3.5 w-3.5" />}
 Resend
 </Button>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 </section>
 </div>
 </AdminPageShell>
 );
}

function ClaimStatus({ row }: { row: NotifRow | undefined }) {
 if (!row) return <span className="text-muted-foreground italic">not attempted</span>;
 if (row.send_status === "sent") {
 return <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Sent {row.sent_at ? new Date(row.sent_at).toLocaleString() : ""}</span>;
 }
 if (row.send_status === "blocked") {
 return <span className="text-amber-700 inline-flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> Blocked: {row.error_message ?? "unknown"}</span>;
 }
 if (row.send_status === "failed") {
 return <span className="text-destructive inline-flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> Failed: {row.error_message ?? "unknown"}</span>;
 }
 return <span className="text-muted-foreground">{row.send_status}</span>;
}
